<template>
  <!-- 레이어(절대좌표)형 팝업만 렌더링 (window타입은 window.open으로 처리) -->
  <div v-if="variant === 'layer' && open" class="lypop" :style="styleLayer">
    <!-- 헤더 (드래그 가능) -->
    <div class="header" @mousedown="startDrag" style="cursor: move; background: #f5f5f5; padding: 8px; border-bottom: 1px solid #ddd; user-select: none;">
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <span style="font-weight: bold;">{{ title }}</span>
        <a href="#" style="color: #666; text-decoration: none; font-size: 18px;" @click.prevent="close">×</a>
      </div>
    </div>
    <!-- 콘텐츠 -->
    <div class="contents" v-dompurify-html="html"></div>
    <!-- 하단 바 -->
    <div class="btm" style="background:#fff; padding:5px; height:35px; display:flex; align-items:center; gap:8px;">
      <template v-if="stopVewAt">
        <label style="color:#000;">
          <input type="checkbox" @change="onToggleToday" />
          오늘하루 열지않음
        </label>
      </template>
      <a href="#" class="btn_close" @click.prevent="close">창닫기</a>
    </div>
  </div>
</template>
<script setup>
import { computed, watch, ref, onBeforeUnmount } from 'vue'
import { getCookie, setCookie } from '@/utils/cookie'
import '@/assets/css/popup.css'

const props = defineProps({
  modelValue: { type: Boolean, default: true },      // v-model 제어
  popup: { type: Object, required: true },           // { popupId, variant, contentHtml, title, left, top, width, height, zIndex, stopVewAt, expireDays, visible }
})

const emit = defineEmits(['update:modelValue', 'close'])

const id = computed(() => props.popup?.popupId || '')
const variant = computed(() => {
  const v = props.popup?.variant || 'layer'
  return v
})
const title = computed(() => props.popup?.title || '')
const html = computed(() => props.popup?.contentHtml || '')
const stopVewAt = computed(() => props.popup?.stopVewAt === 'Y')
const expireDays = computed(() => Number(props.popup?.expireDays || 1))

// 드래그 상태
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const currentPos = ref({ left: 0, top: 0 })

// 초기 위치 설정
watch(() => props.popup, (popup) => {
  if (popup) {
    currentPos.value = {
      left: popup.left ?? 50,
      top: popup.top ?? 50
    }
  }
}, { immediate: true })

const styleLayer = computed(() => {
  const style = {
    position: 'absolute',
    left: currentPos.value.left + 'px',
    top: currentPos.value.top + 'px',
    width: (props.popup?.width ?? 400) + 'px',
    height: (props.popup?.height ?? 500) + 'px',
    zIndex: props.popup?.zIndex ?? 9999,
    border: '1px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
  }
  return style
})

function isBlockedToday() {
  if (!stopVewAt.value || !id.value) return false
  return getCookie(`POP_STOP_${id.value}`) === '1'
}

// open을 computed로 관리 (layer 팝업만 처리)
const open = computed({
  get() {
    return props.modelValue && !isBlockedToday()
  },
  set(v) {
    emit('update:modelValue', v)
  }
})

// 드래그 시작
function startDrag(e) {
  if (e.button !== 0) return // 좌클릭만
  isDragging.value = true
  
  const rect = e.currentTarget.closest('.lypop').getBoundingClientRect()
  dragOffset.value = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }
  
  document.addEventListener('mousemove', onDragMove)
  document.addEventListener('mouseup', onDragEnd)
}

// 드래그 중
function onDragMove(e) {
  if (!isDragging.value) return
  
  currentPos.value = {
    left: e.clientX - dragOffset.value.x,
    top: e.clientY - dragOffset.value.y
  }
}

// 드래그 종료
function onDragEnd() {
  isDragging.value = false
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
}

// 정리
onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
})

function close() {
  open.value = false
  emit('close')
}

function onToggleToday(e) {
  if (!stopVewAt.value || !id.value) return
  const checked = e?.target?.checked
  if (checked) {
    const days = Math.max(1, expireDays.value|0)
    setCookie(`POP_STOP_${id.value}`, '1', { days })
  } else {
    setCookie(`POP_STOP_${id.value}`, '', { days: -1 }) // 삭제
  }
}
</script>
<style scoped>
.lypop { 
  border: 1px solid #ddd; 
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  margin-left: 0;
}
.header {
  cursor: move;
  user-select: none;
}
.contents { 
  padding: 12px; 
  overflow: auto;
  flex: 1;
}
.btm { 
  border-top: 1px solid #eee;
  flex-shrink: 0;
}
.btm .btn_close { 
  margin-left: auto; 
  color:#333; 
  text-decoration: underline; 
}

/* 모바일: 뷰포트를 벗어나지 않도록 보정 */
@media (max-width: 768px) {
  .lypop {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%);
    max-width: 95vw !important;
    max-height: 90vh !important;
  }
}
</style>
